import { useState, useCallback } from 'react';
import {
  fetchEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  type EmployeeInput,
  type EmployeeFilters,
} from '../services/master.service';
import type { Employee } from '../types';

export function useEmployee() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadEmployees = useCallback(async (filters?: EmployeeFilters) => {
    setLoading(true);
    setError(null);
    try {
      setEmployees(await fetchEmployees(filters));
    } catch (e) {
      setError(e instanceof Error ? e.message : '社員の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  const addEmployee = useCallback(async (data: EmployeeInput): Promise<Employee> => {
    const created = await createEmployee(data);
    setEmployees((prev) => [created, ...prev]);
    return created;
  }, []);

  const editEmployee = useCallback(async (id: string, data: Partial<EmployeeInput>): Promise<Employee> => {
    const updated = await updateEmployee(id, data);
    setEmployees((prev) => prev.map((x) => (x.id === id ? updated : x)));
    return updated;
  }, []);

  const removeEmployee = useCallback(async (id: string): Promise<void> => {
    await deleteEmployee(id);
    setEmployees((prev) => prev.filter((x) => x.id !== id));
  }, []);

  return { employees, loading, error, loadEmployees, addEmployee, editEmployee, removeEmployee };
}
